import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { cn } from 'support/helpers/cn/cn';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { WebEDIRowData } from 'support/types/webediTable';
import { useValue } from 'components/WebEDI/helpers';

import { overflowTooltipTarget } from '../../../components/OverflowTooltip/overflowTooltipTarget';
import { CellProps } from '../../types';
import { getCellNestedLevel, useCellClassNames } from '../cellHelpers';
import { NestedLevelAddon } from '../components/NestedLevelAddon/NestedLevelAddon';
import { ViewWrapperCell } from './ViewWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> &
  Pick<CellProps, 'fieldId' | 'isHighlighted' | 'diffStatus' | 'originalValue' | 'wasAIEdited'>;

export const ViewTextCell = ({
  row,
  cell,
  column,
  table,
  isHighlighted,
  fieldId,
  diffStatus,
  originalValue,
  wasAIEdited,
}: Props) => {
  const { t } = useTranslation();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const error = service.useItemByFieldPath(fieldId);
  const value = useValue(row.original, fieldId, column.columnDef.meta?.optionalConfig?.expression);
  const {
    columnDef: { meta: columnMeta },
  } = column;

  const formatValue = useMemo(
    () => columnMeta?.optionalConfig?.translateValue || ((value: any) => value),
    [columnMeta?.optionalConfig?.translateValue],
  );

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });
  const cellNestedLevel = columnMeta?.showNestLevel ? getCellNestedLevel(row) : 0;

  return (
    <ViewWrapperCell
      className={cellClassNames}
      isHighlighted={isHighlighted}
      fieldId={fieldId}
      meta={columnMeta}
      cell={cell}
      diffStatus={diffStatus}
      originalValue={originalValue}
      formattedOriginalValue={originalValue != null ? formatValue(originalValue) : undefined}
      textAlign={columnMeta?.optionalConfig?.textAlign}
      wasAIEdited={wasAIEdited}
    >
      <span className="flex w-full min-w-0 items-center">
        <div
          className={cn('flex w-full min-w-0 gap-x-0.5 overflow-hidden', {
            'justify-start': columnMeta?.optionalConfig?.textAlign === 'left',
          })}
        >
          {cellNestedLevel > 1 ? <NestedLevelAddon level={cellNestedLevel} className="pr-1.5" /> : null}
          {!value && error?.type === 'required' ? (
            t('common:table.errors.requiredField')
          ) : value ? (
            <span className="flex-initial truncate" {...overflowTooltipTarget}>
              {formatValue(value)}
            </span>
          ) : (
            <span className={cn({ 'text-gray-400': !error })}>{EMPTY_FIELD}</span>
          )}
        </div>
      </span>
    </ViewWrapperCell>
  );
};
